import { DownOutlined } from '@ant-design/icons';
import AppButton from '@nxsys/components/AppButton';
import AppModalContainer from '@nxsys/components/AppModalContainer';
import AppsContent from '@nxsys/components/AppsContainer/AppsContent';
import AppsHeader from '@nxsys/components/AppsContainer/AppsHeader';
import AppTableContainer from '@nxsys/components/AppTableContainer';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppDispatch, AppState } from '@nxsys/store';
import { rpnActions } from '@nxsys/store/slices/rpn/reducer/rpnSlice';
import { UpdateRPNByEmployeeIdsResponse } from '@nxsys/store/slices/rpn/types';
import jwtAxios from '@nxsys/services/axios';
import { endPoints } from '@nxsys/services/endpoints';
import { showToast } from '@nxsys/utils/toast';
import { Dropdown } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import moment from 'moment';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

type Props = {
  isOpen: boolean;
  toggle: () => void;
  taxPeriod?: number | null;
  payFrequency?: string | null;
};

const RPNValidationModal: React.FC<Props> = ({
  isOpen,
  toggle,
  taxPeriod,
  payFrequency,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { messages } = useTypedIntl();
  const { isBureauEmployer, user } = useSelector((state: AppState) => state.appStore);

  const [employeeList, setEmployeeList] = useState<any[]>([]);
  const [pagination, setPagination] = useState({ current: 0, pageSize: 20, totalDocs: 0 });
  const [loading, setLoading] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [selectedRows, setSelectedRows] = useState<any[]>([]);
  const [updateRPNResponse, setUpdateRPNResponse] = useState<UpdateRPNByEmployeeIdsResponse>({
    listEmployeeSucceed: [],
    listEmployeeFailed: [],
  });

  const fetchData = useCallback(
    async (page = 0, pageSize = 20) => {
      setLoading(true);
      try {
        const params: Record<string, any> = {
          page,
          size: pageSize,
          paymentModel: 'PAYEE',
          invoiceStatus: 'AWAITING_DRAFT',
          onlySavedRunPayroll: true,
          appName: 'BUREAU',
        };
        if (taxPeriod) params.taxPeriod = taxPeriod;
        if (payFrequency) params.payFrequency = payFrequency;
        if (isBureauEmployer && user?.companyUserIds?.length) {
          params.companyIds = user.companyUserIds;
        }

        const response = await jwtAxios.get(endPoints.employee.getV2, { params });
        setEmployeeList(response.data.data ?? []);
        setPagination({
          current: page,
          pageSize,
          totalDocs: response.data.totalDocs ?? 0,
        });
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    },
    [taxPeriod, payFrequency, isBureauEmployer, user],
  );

  useEffect(() => {
    if (isOpen) {
      setUpdateRPNResponse({ listEmployeeSucceed: [], listEmployeeFailed: [] });
      setSelectedRows([]);
      fetchData(0, pagination.pageSize);
    }
  }, [isOpen]);

  const handleUpdateRPN = async (ids: string[]) => {
    setIsUpdating(true);
    try {
      const res = await dispatch(rpnActions.updateRPNByEmployeeIds({ ids })).unwrap();
      const { listEmployeeFailed = [], listEmployeeSucceed = [] } = res;

      if (listEmployeeFailed.length > 0 && listEmployeeSucceed.length === 0) {
        showToast({
          type: 'error',
          message:
            listEmployeeFailed.length === 1
              ? listEmployeeFailed[0]?.errorMsg ?? ''
              : messages['rpn.update.getError'],
        });
      } else if (listEmployeeFailed.length === 0 && listEmployeeSucceed.length > 0) {
        showToast({ type: 'success', message: messages['rpn.update.getSuccess'] });
      } else if (listEmployeeFailed.length > 0 && listEmployeeSucceed.length > 0) {
        showToast({ type: 'warning', message: messages['rpn.update.getSuccessError'] });
      }

      setUpdateRPNResponse((prev) => {
        const failedIds = listEmployeeFailed.map((i) => i?.subcontractor?.id);
        const successIds = listEmployeeSucceed.map((i) => i?.subcontractor?.id);
        return {
          listEmployeeSucceed: [
            ...prev.listEmployeeSucceed.filter((i) => !failedIds.includes(i?.subcontractor?.id)),
            ...listEmployeeSucceed,
          ],
          listEmployeeFailed: [
            ...prev.listEmployeeFailed.filter((i) => !successIds.includes(i?.subcontractor?.id)),
            ...listEmployeeFailed,
          ],
        };
      });
      fetchData(pagination.current, pagination.pageSize);
    } catch (error) {
      console.error(error);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleUpdateAll = async () => {
    setIsUpdating(true);
    try {
      const response = await jwtAxios.get(endPoints.employee.getV2, {
        params: {
          page: 0,
          size: pagination.totalDocs || 100,
          paymentModel: 'PAYEE',
          invoiceStatus: 'AWAITING_DRAFT',
          onlySavedRunPayroll: true,
          appName: 'BUREAU',
          ...(taxPeriod ? { taxPeriod } : {}),
          ...(payFrequency ? { payFrequency } : {}),
          ...(isBureauEmployer && user?.companyUserIds?.length
            ? { companyIds: user.companyUserIds }
            : {}),
        },
      });
      const ids = (response.data.data ?? []).map((item: any) => item.id);
      await handleUpdateRPN(ids);
    } catch (error) {
      console.error(error);
      setIsUpdating(false);
    }
  };

  const handleUpdateSelected = () => {
    if (!selectedRows.length) {
      showToast({ type: 'warning', message: messages['common.selectAtLeastOneToExport'] });
      return;
    }
    handleUpdateRPN(selectedRows.map((r) => r.id));
  };

  const handleRequestNew = async (ids: string[]) => {
    setIsUpdating(true);
    try {
      const res = await dispatch(rpnActions.updateRPNByEmployeeIds({ ids })).unwrap();
      const { listEmployeeFailed = [], listEmployeeSucceed = [] } = res;

      setUpdateRPNResponse((prev) => {
        const successIds = listEmployeeSucceed.map((i) => i?.subcontractor?.id);
        return {
          listEmployeeSucceed: [...prev.listEmployeeSucceed, ...listEmployeeSucceed],
          listEmployeeFailed: [
            ...prev.listEmployeeFailed.filter((i) => !successIds.includes(i?.subcontractor?.id)),
            ...listEmployeeFailed,
          ],
        };
      });
      if (listEmployeeSucceed.length > 0) {
        showToast({ type: 'success', message: 'Request New RPN successful' });
      }
      fetchData(pagination.current, pagination.pageSize);
    } catch (error) {
      console.error(error);
    } finally {
      setIsUpdating(false);
    }
  };

  const getRowClassName = (record: any) => {
    const isFailed = updateRPNResponse.listEmployeeFailed?.find(
      (item) => item?.subcontractor?.id === record?.id,
    );
    if (isFailed) return 'error-row';
    if (!record?.lastRpnId) return 'no-rpn-row';
    const isSucceeded = updateRPNResponse.listEmployeeSucceed?.find(
      (item) => item?.subcontractor?.id === record?.id,
    );
    if (isSucceeded) return 'success-row';
    return '';
  };

  const columns: ColumnsType<any> = [
    {
      key: 'id',
      title: 'Employee ID',
      dataIndex: 'id',
    },
    {
      key: 'employeeName',
      title: 'Employee Name',
      render: (record) => record?.fullName ?? `${record?.forename ?? ''} ${record?.surname ?? ''}`,
    },
    {
      key: 'rpnStatus',
      title: 'RPN Status',
      align: 'center',
      render: (record) => {
        const isFailed = updateRPNResponse.listEmployeeFailed?.find(
          (item) => item?.subcontractor?.id === record?.id,
        );
        if (isFailed) return 'Failed';
        if (!record?.lastRpnId) return 'No RPN';
        const isSucceeded = updateRPNResponse.listEmployeeSucceed?.find(
          (item) => item?.subcontractor?.id === record?.id,
        );
        if (isSucceeded) return 'Updated';
        return 'Active';
      },
    },
    {
      key: 'lastRpnDate',
      title: 'Last RPN Date',
      align: 'right',
      render: (record) =>
        record?.lastRpnDate ? moment(record.lastRpnDate).format('DD/MM/YYYY') : '_',
    },
    {
      key: 'errorMessage',
      title: 'Error Message',
      width: 300,
      render: (record) => {
        const isFailed = updateRPNResponse.listEmployeeFailed?.find(
          (item) => item?.subcontractor?.id === record?.id,
        );
        if (isFailed) return isFailed?.errorMsg ?? '';
        if (!record?.lastRpnId) return messages['rpn.noRPNNeedRequest'];
        return '';
      },
    },
  ];

  const hasFailed = updateRPNResponse.listEmployeeFailed.length > 0;
  const failedIds = updateRPNResponse.listEmployeeFailed.map((i) => i?.subcontractor?.id);

  const updateMenuItems = [
    { key: 'all', label: 'All', onClick: handleUpdateAll },
    { key: 'selected', label: 'Selected', onClick: handleUpdateSelected },
  ];

  const requestNewMenuItems = [
    {
      key: 'all',
      label: 'All Failed',
      onClick: () => handleRequestNew(failedIds),
    },
    {
      key: 'selected',
      label: 'Selected Failed',
      onClick: () => {
        const ids = selectedRows
          .filter((r) => failedIds.includes(r.id))
          .map((r) => r.id);
        if (!ids.length) {
          showToast({ type: 'warning', message: messages['common.selectAtLeastOneToExport'] });
          return;
        }
        handleRequestNew(ids);
      },
    },
  ];

  const onChangePagination = (page: number, pageSize: number) => {
    const isPageSizeChanged = pageSize !== pagination.pageSize;
    const shouldResetPage = isPageSizeChanged && pagination.current > 0;
    fetchData(shouldResetPage ? 0 : page - 1, pageSize);
  };

  return (
    <AppModalContainer
      title="Update RPN"
      isOpen={isOpen}
      toggle={toggle}
      width="90vw"
      footer={null}
    >
      <AppsHeader>
        <div className="flex justify-end gap-x-2 w-full">
          <Dropdown menu={{ items: updateMenuItems }} disabled={isUpdating}>
            <AppButton
              type="primary"
              name="Update"
              rightIcon={<DownOutlined />}
              loading={isUpdating}
            />
          </Dropdown>
          {hasFailed && (
            <Dropdown menu={{ items: requestNewMenuItems }} disabled={isUpdating}>
              <AppButton name="Request New RPN" rightIcon={<DownOutlined />} />
            </Dropdown>
          )}
        </div>
      </AppsHeader>
      <AppsContent>
        <AppTableContainer
          data={employeeList}
          loading={loading}
          page={pagination.current}
          total={pagination.totalDocs}
          pageSize={pagination.pageSize}
          scroll={{ x: 1200, y: 500 }}
          titleEmpty="No employees found"
          columns={columns}
          onChangePagination={onChangePagination}
          rowClassName={getRowClassName}
          rowSelection={{
            type: 'checkbox',
            onChange: (_: React.Key[], rows: any[]) => setSelectedRows(rows),
          }}
        />
      </AppsContent>
    </AppModalContainer>
  );
};

export default RPNValidationModal;
